/**
 * Mansione ↔ tipologie di turno ammesse, con peso per combinazione
 * tipologia×giorni (passo 2 del motore di generazione automatica copertura,
 * vedi docs/DESIGN.md). Il peso non è una percentuale: è un valore relativo
 * alle altre righe della stessa mansione, quindi l'UI mostra sempre una
 * "quota" calcolata live mentre l'utente modifica i pesi — è il modo più
 * intuitivo di far capire l'effetto di un numero altrimenti astratto.
 * Righe globali (non per RSA): stesso schema per tutte le strutture.
 */
import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import MansioneSelect from "../../components/MansioneSelect";
import TipologiaTurnoSelect from "../../components/TipologiaTurnoSelect";
import { Card, Spinner, ErrorBox } from "../../components/ui/basics";

const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function maskToLabel(mask) {
  if (mask === "1111111") return "Tutti i giorni";
  if (mask === "1111100") return "Lun-Ven";
  if (mask === "0000011") return "Sab-Dom";
  return GIORNI.filter((_, i) => mask?.[i] === "1").join(", ") || "—";
}

function GiorniToggle({ value, onChange }) {
  const bits = (value || "0000000").split("");
  return (
    <div className="flex gap-1">
      {GIORNI.map((g, i) => (
        <button
          key={g}
          type="button"
          onClick={() => {
            const next = [...bits];
            next[i] = next[i] === "1" ? "0" : "1";
            onChange(next.join(""));
          }}
          className={`px-2 py-1 rounded text-xs border ${
            bits[i] === "1"
              ? "bg-primary-600 text-white border-primary-600"
              : "bg-white text-gray-500 border-gray-300"
          }`}
        >
          {g}
        </button>
      ))}
    </div>
  );
}

function pesoTotaleAttive(rows) {
  return rows
    .filter((r) => r.attivo)
    .reduce((s, r) => s + (Number(r.peso) || 0), 0);
}

function quotaLabel(row, totale) {
  if (!row.attivo || !totale) return "—";
  const pct = Math.round(((Number(row.peso) || 0) / totale) * 1000) / 10;
  return `${pct}%`;
}

const FORM_INIT = { giornimask: "1111111", peso: "1" };

export default function MansioneTurnoSection() {
  const { apiRequest } = useAuth();
  const [mansione, setMansione] = useState(null);
  const endpoint = mansione
    ? `/planning/mansioni-turni?mansione=${encodeURIComponent(mansione)}&tutte=1`
    : null;
  const { data, loading, error, reload } = useApi(endpoint);

  // Peso in editing (non ancora salvato): tenuto separato dai dati server
  // per calcolare la quota live mentre l'utente digita, prima del PATCH.
  const [editPeso, setEditPeso] = useState({});
  const [esempioOre, setEsempioOre] = useState("100");
  const [form, setForm] = useState(FORM_INIT);
  const [tipologiaForm, setTipologiaForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const rows = (data?.items || []).map((r) => ({
    ...r,
    peso: editPeso[r.id] !== undefined ? editPeso[r.id] : r.peso,
  }));
  const totale = pesoTotaleAttive(rows);
  const oreEsempio = Number(esempioOre) || 0;

  const salvaPeso = async (row) => {
    const nuovoPeso = Number(editPeso[row.id]);
    if (Number.isNaN(nuovoPeso) || nuovoPeso < 0) return;
    await apiRequest(`/planning/mansioni-turni/${row.id}`, {
      method: "PATCH",
      body: JSON.stringify({ peso: nuovoPeso }),
    });
    setEditPeso((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    reload();
  };

  const toggleAttivo = async (row) => {
    await apiRequest(`/planning/mansioni-turni/${row.id}`, {
      method: "PATCH",
      body: JSON.stringify({ attivo: !row.attivo }),
    });
    reload();
  };

  const elimina = async (row) => {
    if (
      !window.confirm(
        `Eliminare la combinazione ${row.turnotipocodice} / ${maskToLabel(row.giornimask)}?`,
      )
    )
      return;
    await apiRequest(`/planning/mansioni-turni/${row.id}`, {
      method: "DELETE",
    });
    reload();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!tipologiaForm) {
      setFormError("Seleziona una tipologia di turno");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await apiRequest("/planning/mansioni-turni", {
        method: "POST",
        body: JSON.stringify({
          mansionecodice: mansione,
          turnotipocodice: tipologiaForm.codice,
          giornimask: form.giornimask,
          peso: Number(form.peso) || 0,
        }),
      });
      setForm(FORM_INIT);
      setTipologiaForm(null);
      reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Mansione ↔ tipologie di turno"
      subtitle="Quali turni sono ammessi per ogni mansione, e con che peso il motore di generazione automatica copertura deve distribuirci le ore richieste."
    >
      <p className="text-xs text-gray-500 mb-3">
        Il peso <strong>non è una percentuale</strong>: conta solo il rapporto
        tra le righe della stessa mansione. Es. "notte" peso 3 e "pomeriggio
        festivo" peso 1 → il motore assegna 3 ore di notte ogni 1 di pomeriggio
        festivo (75%/25%). La colonna "Quota" si aggiorna subito mentre
        modifichi i pesi, per farti vedere l'effetto prima di salvare.
      </p>

      <label className="text-sm block mb-4">
        <span className="block text-xs text-gray-500 mb-1">Mansione</span>
        <MansioneSelect
          value={mansione}
          onChange={setMansione}
          className="border border-gray-300 rounded px-2 py-1.5 w-56"
        />
      </label>

      {!mansione && (
        <p className="text-sm text-gray-400 py-4 text-center">
          Seleziona una mansione per configurare i turni ammessi.
        </p>
      )}
      {mansione && loading && <Spinner />}
      {mansione && error && <ErrorBox message={error} onRetry={reload} />}

      {mansione && data && (
        <>
          {data.source === "default" && (
            <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-3">
              ⚠ Entità non ancora creata sul tenant Dataverse (gate schema).
            </p>
          )}
          {rows.length === 0 && (
            <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-3">
              ⚠ Nessuna combinazione configurata per <strong>{mansione}</strong>
              : il motore di generazione automatica non potrà proporre una
              copertura per questa mansione finché non aggiungi almeno una riga.
            </p>
          )}
          {rows.length > 0 && totale === 0 && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">
              ⚠ Tutti i pesi delle righe attive sono a 0: il motore non saprà
              come distribuire le ore per <strong>{mansione}</strong>.
            </p>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                    <th className="py-2 px-3">Tipologia</th>
                    <th className="py-2 px-3">Giorni</th>
                    <th className="py-2 px-3 text-right">Peso</th>
                    <th className="py-2 px-3 text-right">Quota</th>
                    <th className="py-2 px-3 text-right">
                      Es. ore/sett su{" "}
                      <input
                        type="number"
                        min="0"
                        value={esempioOre}
                        onChange={(e) => setEsempioOre(e.target.value)}
                        className="border border-gray-300 rounded px-1.5 py-0.5 w-16 text-right"
                      />
                    </th>
                    <th className="py-2 px-3">Attiva</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2 px-3">{r.turnotipocodice}</td>
                      <td className="py-2 px-3">{maskToLabel(r.giornimask)}</td>
                      <td className="py-2 px-3 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={
                            editPeso[r.id] !== undefined
                              ? editPeso[r.id]
                              : r.peso
                          }
                          onChange={(e) =>
                            setEditPeso({
                              ...editPeso,
                              [r.id]: e.target.value,
                            })
                          }
                          onBlur={() => {
                            if (editPeso[r.id] !== undefined) salvaPeso(r);
                          }}
                          className="border border-gray-300 rounded px-2 py-1 w-20 text-right"
                        />
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {quotaLabel(r, totale)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-gray-500">
                        {r.attivo && totale
                          ? Math.round(
                              (oreEsempio * (Number(r.peso) || 0)) / totale,
                            )
                          : "—"}
                      </td>
                      <td className="py-2 px-3">
                        <button
                          type="button"
                          onClick={() => toggleAttivo(r)}
                          className="text-xs underline text-gray-600"
                        >
                          {r.attivo ? "Disattiva" : "Attiva"}
                        </button>
                      </td>
                      <td className="py-2 px-3">
                        <button
                          type="button"
                          onClick={() => elimina(r)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Elimina
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-gray-400 mt-1">
                L'anteprima ore/sett è indicativa (quota lineare): la
                generazione reale arrotonda poi a un numero intero di persone
                per riga, con un piccolo scarto sempre riportato esplicitamente.
              </p>
            </div>
          )}

          <form
            onSubmit={submit}
            className="space-y-3 border-t border-gray-100 pt-3"
          >
            <div className="flex flex-wrap gap-3 items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1">
                  Tipologia turno
                </span>
                <TipologiaTurnoSelect
                  value={tipologiaForm?.codice}
                  onChange={setTipologiaForm}
                  required
                  className="border border-gray-300 rounded px-2 py-1.5 w-56"
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1">Peso</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.peso}
                  onChange={(e) => setForm({ ...form, peso: e.target.value })}
                  className="border border-gray-300 rounded px-2 py-1.5 w-20"
                />
              </label>
            </div>
            <GiorniToggle
              value={form.giornimask}
              onChange={(giornimask) => setForm({ ...form, giornimask })}
            />
            {formError && <p className="text-xs text-red-600">{formError}</p>}
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "+ Aggiungi combinazione"}
            </button>
          </form>
        </>
      )}
    </Card>
  );
}
